/**
 * Pinned Messages Hooks
 * TanStack Query hooks for pinned message operations
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import * as pinnedMessagesService from '@/services/pinned-messages.service';

/**
 * Query hook to get all pinned messages for a channel
 */
export const usePinnedMessages = (channelId: string | null) => {
  return useQuery({
    queryKey: ['pinnedMessages', channelId],
    queryFn: () => pinnedMessagesService.getPinnedMessages(channelId!),
    enabled: !!channelId,
    staleTime: 60 * 1000, // 1 minute (pinned messages don't change as frequently)
  });
};

/**
 * Mutation hook to pin a message
 */
export const usePinMessage = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ channelId, messageId }: { channelId: string; messageId: string }) =>
      pinnedMessagesService.pinMessage(channelId, messageId),
    onSuccess: (_, { channelId }) => {
      // Invalidate pinned messages query to refetch
      queryClient.invalidateQueries({ queryKey: ['pinnedMessages', channelId] });
      toast.success('Съобщението е закачено');
    },
    onError: (error) => {
      console.error('Failed to pin message:', error);
      toast.error('Грешка при закачане на съобщение');
    },
  });
};

/**
 * Mutation hook to unpin a message
 */
export const useUnpinMessage = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ channelId, messageId }: { channelId: string; messageId: string }) =>
      pinnedMessagesService.unpinMessage(channelId, messageId),
    onSuccess: (_, { channelId }) => {
      // Invalidate pinned messages query to refetch
      queryClient.invalidateQueries({ queryKey: ['pinnedMessages', channelId] });
      toast.success('Съобщението е откачено');
    },
    onError: (error) => {
      console.error('Failed to unpin message:', error);
      toast.error('Грешка при откачане на съобщение');
    },
  });
};

/**
 * Helper to check if a message is pinned
 */
export const useIsMessagePinned = (
  channelId: string | null,
  messageId: string
): boolean => {
  const { data: pinnedMessages } = usePinnedMessages(channelId);

  if (!pinnedMessages) return false;

  return pinnedMessages.some((pin) => pin.message_id === messageId);
};
